'use client';

import { Copy } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import { CurrencyInput } from '@kit/ui/currency-input';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { SettingToggleRow } from '@kit/ui/setting-toggle-row';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { CURRENCIES } from '../../constants/audition.constants';
import type { UpdateAuditionInput } from '../../schemas/audition.schema';
import type { AuditionWithDates } from '../../types/audition.types';
import { DateTimeField } from './date-time-field';

interface ApplicationSettingsTabProps {
  form: UseFormReturn<UpdateAuditionInput>;
  /** The row backing this editor — needed for the shareable public link. */
  audition: AuditionWithDates;
  /**
   * Whether the current user is a super-admin. Gates the editorial `featured`
   * toggle (pin to top of public listings). UI hiding is not the security
   * boundary — the write is stripped server-side in `updateAuditionAction`.
   */
  isSuperAdmin?: boolean;
}

export function ApplicationSettingsTab({
  form,
  audition,
  isSuperAdmin = false,
}: ApplicationSettingsTabProps) {
  const { t } = useTranslation();

  // The shareable public link + access code only matter when the competition
  // gates applications by invitation. Watch the live toggle so the block
  // appears/disappears as the organizer flips it, before saving.
  const byInvitationOnly = form.watch('byInvitationOnly');
  // Watch the live code so the share link updates as the organizer edits it.
  const accessCode = (form.watch('accessCode') ?? '').trim();

  // The public page only exists once the record is published (open) and has a
  // slug; until then there is no link to share. When an access code is set we
  // embed it as `?code=` so the copied link auto-unlocks the Apply button in one
  // click (the public page reads `?code=` and self-verifies) — no manual entry.
  const shareUrl =
    audition.status === 'open' && audition.slug
      ? `${typeof window !== 'undefined' ? window.location.origin : ''}/competitions/${audition.slug}` +
        (accessCode ? `?code=${encodeURIComponent(accessCode)}` : '')
      : null;

  const copyShareUrl = () => {
    if (shareUrl) {
      void navigator.clipboard.writeText(shareUrl);
      toast.success(t('auditions:formTabs.appSettings.shareLink.copied'));
    }
  };

  const copyAccessCode = () => {
    const code = (form.getValues('accessCode') ?? '').trim();
    if (code) {
      void navigator.clipboard.writeText(code);
      toast.success(t('explore:partners.accessCode.copied'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Application Period */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:formTabs.appSettings.period.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:formTabs.appSettings.period.description" />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="openingDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="auditions:formTabs.appSettings.openingDate" />
                  </FormLabel>
                  <FormControl>
                    <DateTimeField
                      value={field.value ? new Date(field.value) : null}
                      onChange={field.onChange}
                      placeholder={t(
                        'auditions:formTabs.appSettings.pickDateTime',
                      )}
                      timeLabel={t('auditions:formTabs.appSettings.timeLabel')}
                    />
                  </FormControl>
                  <FormDescription>
                    <Trans i18nKey="auditions:formTabs.appSettings.openingDateHint" />
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="closingDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="auditions:formTabs.appSettings.closingDate" />
                  </FormLabel>
                  <FormControl>
                    <DateTimeField
                      value={field.value ? new Date(field.value) : null}
                      onChange={field.onChange}
                      placeholder={t(
                        'auditions:formTabs.appSettings.pickDateTime',
                      )}
                      timeLabel={t('auditions:formTabs.appSettings.timeLabel')}
                    />
                  </FormControl>
                  <FormDescription>
                    <Trans i18nKey="auditions:formTabs.appSettings.closingDateHint" />
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="autoClose"
            render={({ field }) => (
              <FormItem>
                <SettingToggleRow
                  label={
                    <Trans i18nKey="auditions:formTabs.appSettings.autoClose.label" />
                  }
                  description={
                    <Trans i18nKey="auditions:formTabs.appSettings.autoClose.description" />
                  }
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                  className="border-t pt-4"
                />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      {/* Application Fee */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:formTabs.appSettings.fee.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:formTabs.appSettings.fee.description" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="applicationFeeAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="auditions:formTabs.appSettings.feeAmount" />
                  </FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value ?? undefined}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={field.ref}
                      placeholder="0.00"
                    />
                  </FormControl>
                  <FormDescription>
                    <Trans i18nKey="auditions:formTabs.appSettings.feeAmountHint" />
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="applicationFeeCurrency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="auditions:formTabs.appSettings.currency" />
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || 'USD'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            'auditions:formTabs.appSettings.currencyPlaceholder',
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CURRENCIES.map((currency) => (
                        <SelectItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Application Settings */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:formTabs.appSettings.settings.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:formTabs.appSettings.settings.description" />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* isSuperAdmin gates the editorial `featured` pin-to-top toggle:
              hidden entirely for non-super-admins, and the write is also
              stripped server-side in updateAuditionAction (UI hiding is not the
              security boundary). */}
          {isSuperAdmin && (
            <FormField
              control={form.control}
              name="featured"
              render={({ field }) => (
                <FormItem>
                  <SettingToggleRow
                    label={
                      <Trans i18nKey="auditions:formTabs.appSettings.featured.label" />
                    }
                    description={
                      <Trans i18nKey="auditions:formTabs.appSettings.featured.description" />
                    }
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                  />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="byInvitationOnly"
            render={({ field }) => (
              <FormItem>
                <SettingToggleRow
                  label={
                    <Trans i18nKey="auditions:formTabs.appSettings.byInvitation.label" />
                  }
                  description={
                    <Trans i18nKey="auditions:formTabs.appSettings.byInvitation.description" />
                  }
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </FormItem>
            )}
          />

          {/* Sharing block — only relevant once applications are gated by
              invitation. Surfaces the public link + the access code that
              unlocks the Apply button, so managers can share both from here.
              (Replaces the former partner-portal Invitations tab.) */}
          {byInvitationOnly && (
            <div className="bg-muted/40 space-y-4 rounded-lg p-4">
              <div className="space-y-0.5">
                <p className="text-base font-medium">
                  <Trans i18nKey="auditions:formTabs.appSettings.shareLink.title" />
                </p>
                <p className="text-muted-foreground text-sm">
                  <Trans i18nKey="auditions:formTabs.appSettings.shareLink.description" />
                </p>
              </div>

              {shareUrl ? (
                <div className="flex gap-2">
                  <Input readOnly value={shareUrl} className="font-mono" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={copyShareUrl}
                    aria-label={t(
                      'auditions:formTabs.appSettings.shareLink.copy',
                    )}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  <Trans i18nKey="auditions:formTabs.appSettings.shareLink.notPublished" />
                </p>
              )}

              <FormField
                control={form.control}
                name="accessCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans i18nKey="explore:partners.accessCode.title" />
                    </FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          maxLength={50}
                          placeholder={t(
                            'explore:partners.accessCode.placeholder',
                          )}
                          className="font-mono uppercase"
                        />
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={copyAccessCode}
                        aria-label={t('explore:partners.accessCode.copied')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <FormDescription>
                      <Trans i18nKey="explore:partners.accessCode.description" />
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Auto-accept registrations — competitions only. When on, submitted
              registrations skip organizer review and land directly on Accepted;
              enabling it also retroactively accepts existing Completed rows
              (handled server-side in updateAuditionAction). Never shown for job
              auditions, whose submitted → review → accepted flow is unchanged. */}
          {audition.is_competition && (
            <FormField
              control={form.control}
              name="autoAcceptRegistrations"
              render={({ field }) => (
                <FormItem>
                  <SettingToggleRow
                    label={
                      <Trans i18nKey="auditions:formTabs.appSettings.autoAccept.label" />
                    }
                    description={
                      <Trans i18nKey="auditions:formTabs.appSettings.autoAccept.description" />
                    }
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                    className="border-t pt-4"
                  />
                </FormItem>
              )}
            />
          )}

          {/* Allow entrants to keep editing after acceptance — all record
              types. When off, an accepted registration is frozen (read-only in
              the dancer edit surface, and rejected server-side). When on
              (default), re-submitting an edited accepted registration follows
              the auto-accept setting: back to accepted if auto-accept is on,
              else back to organizer review. */}
          <FormField
            control={form.control}
            name="allowAcceptedEdits"
            render={({ field }) => (
              <FormItem>
                <SettingToggleRow
                  label={
                    <Trans i18nKey="auditions:formTabs.appSettings.allowAcceptedEdits.label" />
                  }
                  description={
                    <Trans i18nKey="auditions:formTabs.appSettings.allowAcceptedEdits.description" />
                  }
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                  className="border-t pt-4"
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="videoPreSelection"
            render={({ field }) => (
              <FormItem>
                <SettingToggleRow
                  label={
                    <Trans i18nKey="auditions:formTabs.appSettings.videoPreselection.label" />
                  }
                  description={
                    <Trans i18nKey="auditions:formTabs.appSettings.videoPreselection.description" />
                  }
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                  className="border-t pt-4"
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="applyOnApp"
            render={({ field }) => (
              <FormItem>
                <SettingToggleRow
                  label={
                    <Trans i18nKey="auditions:formTabs.appSettings.inApp.label" />
                  }
                  description={
                    <Trans i18nKey="auditions:formTabs.appSettings.inApp.description" />
                  }
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                  className="border-t pt-4"
                />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
